import { DEFAULT_PAGE_SIZE } from '../constants/pagination'
import { ApiResponse, Correspondent } from '../types'
import { apiService } from './api.service'

class CorrespondentService {
  private _listAllCache: Promise<ApiResponse<Correspondent>> | null = null

  async getCorrespondents(params?: { page?: number; page_size?: number }): Promise<ApiResponse<Correspondent>> {
    const response = await apiService.get<ApiResponse<Correspondent>>('correspondents/', { params })
    return response.data
  }

  async listAll(): Promise<ApiResponse<Correspondent>> {
    if (!this._listAllCache) {
      this._listAllCache = apiService
        .get<ApiResponse<Correspondent>>('correspondents/', {
          params: { page: 1, page_size: 100000 },
        })
        .then((response) => response.data)
    }
    return this._listAllCache
  }

  clearCache(): void {
    this._listAllCache = null
  }

  async getCorrespondent(id: number): Promise<Correspondent> {
    const response = await apiService.get<Correspondent>(`correspondents/${id}/`)
    return response.data
  }

  async createCorrespondent(data: Partial<Correspondent>): Promise<Correspondent> {
    this.clearCache()
    const response = await apiService.post<Correspondent>('correspondents/', data)
    return response.data
  }

  async updateCorrespondent(id: number, data: Partial<Correspondent>): Promise<Correspondent> {
    this.clearCache()
    const response = await apiService.patch<Correspondent>(`correspondents/${id}/`, data)
    return response.data
  }

  async deleteCorrespondent(id: number): Promise<void> {
    this.clearCache()
    await apiService.delete(`correspondents/${id}/`)
  }

  // Methods for ManagementList compatibility
  async listFiltered(
    page: number,
    _ordering?: string,
    sortField?: string,
    sortReverse?: boolean,
    nameFilter?: string,
    includeDocumentCount?: boolean,
    extraParams?: any
  ): Promise<ApiResponse<Correspondent>> {
    const params: any = {
      page,
      page_size: extraParams?.page_size ?? DEFAULT_PAGE_SIZE,
      full_perms: true, // Include full permission fields
    }

    if (sortField) {
      const prefix = sortReverse ? '-' : ''
      params.ordering = `${prefix}${sortField}`
    }

    if (nameFilter) {
      params.name__icontains = nameFilter
    }

    if (includeDocumentCount) {
      params.include_document_count = true
    }

    if (extraParams) {
      Object.assign(params, extraParams)
    }

    return this.getCorrespondents(params)
  }

  async delete(correspondent: Correspondent): Promise<void> {
    return this.deleteCorrespondent(correspondent.id)
  }

  // Alias methods for EditDialog compatibility
  async create(data: Partial<Correspondent>): Promise<Correspondent> {
    return this.createCorrespondent(data)
  }

  async update(id: number, data: Partial<Correspondent>): Promise<Correspondent> {
    return this.updateCorrespondent(id, data)
  }

  // Bulk edit for permissions
  async bulkEditObjects(objects: number[], operation: string, permissions?: any, merge?: boolean): Promise<void> {
    const params: any = {
      objects,
      object_type: 'correspondents',
      operation,
    }

    if (operation === 'set_permissions' && permissions) {
      params.owner = permissions.owner
      params.permissions = permissions.set_permissions
      params.merge = merge
    }

    await apiService.post('bulk_edit_objects/', params)
  }
}

export const correspondentService = new CorrespondentService()
